import React, { useState, useEffect } from "react";
import { Link } from "react-router-dom";
import { BsGrid3X3GapFill, BsList } from "react-icons/bs";
import { ClipLoader } from "react-spinners";
import { useCategoryContext } from "../context/CategoryContext";
import axios from "axios";
import { SERVER_URL } from '../constants';

const AllSectors = () => {
  const [viewMode, setViewMode] = useState("grid");
  const [searchQuery, setSearchQuery] = useState("");
  const [sortOption, setSortOption] = useState("");
  const { categories, loading, error } = useCategoryContext();
  const [filteredCategories, setFilteredCategories] = useState([]);
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [products, setProducts] = useState([]);
  const [loadingProducts, setLoadingProducts] = useState(false);

  useEffect(() => {
    const updatedCategories = categories.filter((category) =>
      category.name.toLowerCase().includes(searchQuery.toLowerCase())
    ).sort((a, b) => {
      if (sortOption === "alphabetical") return a.name.localeCompare(b.name);
      if (sortOption === "time") return new Date(b.createdAt) - new Date(a.createdAt);
      return 0;
    });
    setFilteredCategories(updatedCategories);
  }, [searchQuery, sortOption, categories]);

  const fetchProductsByCategory = async (categoryName) => {
    setLoadingProducts(true);
    setSelectedCategory(categoryName);
    try {
      const response = await axios.get(`${SERVER_URL}/api/v1/product`);
      const { data } = response.data;
      const filtered = data.filter((product) => product.category === categoryName);
      setProducts(filtered);
    } catch (err) {
      console.error("Error fetching products:", err);
    } finally {
      setLoadingProducts(false);
    }
  };

  return (
    <section className="py-16 px-4 sm:px-6 lg:px-8 bg-gray-50 min-h-screen">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <h1 className="text-3xl sm:text-4xl font-extrabold text-gray-900 text-center mb-10 animate-fade-in">
          Explore <span className="text-blue-600">All Sectors</span>
        </h1>

        {/* Search and Filters */}
        <div className="flex flex-col sm:flex-row justify-between items-center gap-4 mb-12">
          <div className="relative w-full sm:w-1/2">
            <input
              type="text"
              placeholder="Search sectors..."
              className="w-full pl-10 pr-4 py-3 rounded-md border border-gray-300 shadow-sm focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
            />
            <svg
              className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M21 21l-6-6m2-5a7 7 0 11-14 0 7 7 0 0114 0z" />
            </svg>
          </div>
          <div className="flex items-center gap-4">
            <select
              className="border border-gray-300 rounded-md px-4 py-2 text-gray-700 focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all duration-300"
              value={sortOption}
              onChange={(e) => setSortOption(e.target.value)}
            >
              <option value="">Sort By</option>
              <option value="alphabetical">A-Z</option>
              <option value="time">Newest</option>
            </select>
            <div className="flex gap-2 bg-gray-100 p-1 rounded-md">
              <button
                onClick={() => setViewMode("grid")}
                className={`p-2 rounded-md ${viewMode === "grid" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500"} transition-all duration-300`}
              >
                <BsGrid3X3GapFill size={20} />
              </button>
              <button
                onClick={() => setViewMode("list")}
                className={`p-2 rounded-md ${viewMode === "list" ? "bg-white text-blue-600 shadow-sm" : "text-gray-500"} transition-all duration-300`}
              >
                <BsList size={20} />
              </button>
            </div>
          </div>
        </div>

        {/* Categories */}
        {loading ? (
          <div className="flex justify-center items-center h-64">
            <ClipLoader size={40} color={"#2563eb"} />
          </div>
        ) : error ? (
          <p className="text-center text-red-500">Error loading categories</p>
        ) : (
          <div
            className={`grid gap-6 ${viewMode === "grid" ? "grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4" : "grid-cols-1"}`}
          >
            {filteredCategories.map((category, index) => (
              <div
                key={category._id}
                className={`group bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 ${viewMode === "list" ? "flex" : "flex flex-col"} animate-slide-up`}
                style={{ animationDelay: `${index * 100}ms` }}
              >
                <div className={`relative ${viewMode === "list" ? "w-1/3" : "w-full"}`}>
                  <img
                    src={category.imgUrl || '/api/placeholder/300/200'}
                    alt={category.name}
                    className="w-full h-48 object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300" />
                </div>
                <div className={`p-5 ${viewMode === "list" ? "w-2/3" : "w-full"} flex flex-col`}>
                  <h2 className="text-lg font-semibold text-gray-900 group-hover:text-blue-600 transition-colors duration-300">
                    {category.name}
                  </h2>
                  <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                    {category.description || "Explore products in this category."}
                  </p>
                  <button
                    onClick={() => fetchProductsByCategory(category.name)}
                    className="mt-4 w-full bg-blue-600 text-white py-2 rounded-md text-sm font-semibold hover:bg-blue-700 transition-all duration-300"
                  >
                    Show Products
                  </button>
                </div>
              </div>
            ))}
          </div>
        )}

        {/* Products Display */}
        {selectedCategory && (
          <div className="mt-12 animate-fade-in">
            <h2 className="text-2xl font-bold text-gray-900 mb-6">
              Top Products in <span className="text-blue-600">{selectedCategory}</span>
            </h2>
            {loadingProducts ? (
              <div className="flex justify-center items-center h-64">
                <ClipLoader size={40} color={"#2563eb"} />
              </div>
            ) : products.length === 0 ? (
              <p className="text-center text-gray-500">No products found in this category.</p>
            ) : (
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                {products.map((product, index) => (
                  <div
                    key={product._id}
                    className="group bg-white border border-gray-200 rounded-lg shadow-sm hover:shadow-md transition-all duration-300 animate-slide-up"
                    style={{ animationDelay: `${index * 100}ms` }}
                  >
                    <div className="relative overflow-hidden">
                      <img
                        src={product.imgUrl[0] || '/api/placeholder/300/300'}
                        alt={product.name}
                        className="w-full h-56 object-contain p-4 transition-transform duration-500 group-hover:scale-105"
                      />
                      <div className="absolute inset-0 bg-gradient-to-t from-black/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-end justify-center p-4">
                        <Link
                          to={`/product/${product._id}`}
                          className="bg-white text-gray-800 px-4 py-2 rounded-full text-sm font-semibold flex items-center gap-2 hover:bg-gray-100 transition-all duration-300 shadow-md"
                        >
                          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0zm-3 9a9 9 0 100-18 9 9 0 000 18z" />
                          </svg>
                          Quick View
                        </Link>
                      </div>
                      {product.discount && (
                        <span className="absolute top-2 left-2 bg-blue-600 text-white text-xs font-bold px-2 py-1 rounded">
                          {product.discount}% OFF
                        </span>
                      )}
                      {product.totalStock < 10 && (
                        <span className="absolute top-2 right-2 bg-orange-500 text-white text-xs font-semibold px-2 py-1 rounded">
                          Only {product.totalStock} left
                        </span>
                      )}
                    </div>
                    <div className="p-5">
                      <h3 className="text-md font-semibold text-gray-900 line-clamp-2 min-h-[2.5rem] group-hover:text-blue-600 transition-colors duration-300">
                        {product.name}
                      </h3>
                      <p className="text-sm text-gray-600 line-clamp-2 mt-1">
                        {product.description || "A top product in this category."}
                      </p>
                      <div className="flex items-center justify-between mt-4">
                        <div className="flex items-center gap-2">
                          <span className="text-lg font-bold text-gray-900">
                            ₹{product.price.toLocaleString()}
                          </span>
                          {product.originalPrice && (
                            <span className="text-sm text-gray-500 line-through">
                              ₹{product.originalPrice.toLocaleString()}
                            </span>
                          )}
                        </div>
                        <Link
                          to={`/product/${product._id}`}
                          className="bg-blue-600 text-white px-4 py-2 rounded-md text-sm font-semibold hover:bg-blue-700 transition-all duration-300"
                        >
                          View Product
                        </Link>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>
    </section>
  );
};

export default AllSectors;
